import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  TextField,
  Button,
  Avatar,
  Alert,
  Divider,
  Grid
} from '@mui/material';
import { Save, Person, Warning } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { userService } from '../services/api';
import ProfanityAwareTextField from '../components/ProfanityAwareTextField';
import { validateTextForProfanity } from '../utils/profanityFilter';

const Profile = () => {
  const { user, updateUser } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    bio: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (user) {
      // Handle Cognito user attributes structure
      const attributes = user.attributes || {};
      const email = attributes.email || user.email || '';
      const givenName = attributes.given_name || user.given_name || '';
      const familyName = attributes.family_name || user.family_name || '';
      const name = attributes.name || user.name || `${givenName} ${familyName}`.trim() || '';
      
      setFormData({
        name: name,
        email: email,
        bio: attributes.bio || user.bio || ''
      });
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check for profanity before submitting
    const nameValidation = validateTextForProfanity(formData.name);
    const bioValidation = validateTextForProfanity(formData.bio);
    
    if (nameValidation.hasProfanity || bioValidation.hasProfanity) {
      const profaneWords = [...nameValidation.profaneWords, ...bioValidation.profaneWords];
      setError(`Please remove inappropriate language before saving: ${profaneWords.join(', ')}`);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      setSuccess(false);

      // Get user ID from auth context - try multiple possible properties
      const userId = user.sub || user.userId || user.username || user.user_id;
      
      if (!userId) {
        throw new Error('User ID not found');
      }

      // Split name into given_name and family_name for backend
      const nameParts = formData.name.trim().split(' ');
      const given_name = nameParts[0] || '';
      const family_name = nameParts.slice(1).join(' ') || '';

      // Prepare data for backend (backend expects given_name, family_name, not name)
      const updateData = {
        given_name,
        family_name,
        bio: formData.bio
      };

      const response = await userService.updateProfile(userId, updateData);
      
      // Extract the user data from the response (handle nested structure)
      const userData = response.data?.data || response.data;
      
      // Reconstruct full name from given_name and family_name
      const fullName = `${userData.given_name || ''} ${userData.family_name || ''}`.trim();
      
      // Update user object with the new data, ensuring bio is in both places
      const userUpdate = {
        ...userData,
        name: fullName, // Add reconstructed full name
        bio: userData.bio || formData.bio, // Ensure bio is at root level
        attributes: {
          ...user.attributes,
          name: fullName, // Also update name in attributes
          given_name: userData.given_name,
          family_name: userData.family_name,
          bio: userData.bio || formData.bio // Also update in attributes
        }
      };
      
      updateUser(userUpdate);
      
      // Update form data to reflect the saved changes
      setFormData(prev => ({
        ...prev,
        name: fullName
      }));
      
      setSuccess(true);
    } catch (err) {
      setError('Failed to update profile');
      console.error('Error updating profile:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="warning">Please log in to view your profile.</Alert>
      </Container>
    );
  }

  // Extract user info for display
  const attributes = user.attributes || {};
  const email = attributes.email || user.email || '';
  const picture = attributes.picture || user.picture || '';
  const role = user.role || 'user';
  const isAdmin = user.isAdmin || user.role === 'admin';

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <Avatar
            sx={{ width: 80, height: 80, mr: 3, bgcolor: 'primary.main' }}
            src={picture}
          >
            <Person sx={{ fontSize: 40 }} />
          </Avatar>
          <Box>
            <Typography variant="h4" component="h1">
              Profile Settings
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage your account information
            </Typography>
            {isAdmin && (
              <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
                Administrator Account
              </Typography>
            )}
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Profile updated successfully!
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <ProfanityAwareTextField
                fullWidth
                label="Full Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                variant="outlined"
                required
                disabled
                helperText="Name cannot be changed"
                showProfanityWarning={true}
                showCleanSuggestion={true}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                variant="outlined"
                required
                disabled
                helperText="Email cannot be changed"
              />
            </Grid>
            <Grid item xs={12}>
              <ProfanityAwareTextField
                fullWidth
                label="Bio"
                name="bio"
                value={formData.bio}
                onChange={handleInputChange}
                variant="outlined"
                multiline
                rows={4}
                placeholder="Tell us about yourself..."
                helperText="This will be displayed on your public profile"
                showProfanityWarning={true}
                showCleanSuggestion={true}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                User ID: {user.userId || user.sub || 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Role: {isAdmin ? 'Administrator' : 'User'}
              </Typography>
            </Box>
            <Button
              type="submit"
              variant="contained"
              startIcon={<Save />}
              disabled={loading}
              size="large"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </Box>

        {/* Debug Info (remove in production) */}
        {/*
        <Divider sx={{ my: 3 }} />
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Debug Information
          </Typography>
          <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography variant="body2" component="pre" sx={{ fontSize: '0.75rem', overflow: 'auto' }}>
              {JSON.stringify(user, null, 2)}
            </Typography>
          </Paper>
        </Box>
        */}
      </Paper>
    </Container>
  );
};

export default Profile;
